import { ShaderModule, ShaderRenderContext } from './index';

/**
 * Quantum Grid Shader Mode
 *
 * Simulates cosmic ledger telemetry and Riemann coordinate manifold deformation.
 * Features:
 * - 3D perspective matrix grid with dynamic undulating height displacement
 * - Nodal tension gradient calculations shifting lines from calm cyan to stressed amber/magenta
 * - Coordinate rewiring vectors and rotating direction calipers at active nodal intersections
 * - Telemetry glyphs and ledger crosshair indicators
 */
export class QuantumGridShader implements ShaderModule {
  readonly name = 'quantum_grid' as const;

  render(context: ShaderRenderContext): void {
    const { ctx, width, height, time, uniforms, reducedMotion } = context;
    const { speed, intensity, distortion, gridDensity } = uniforms;

    const animSpeed = reducedMotion ? speed * 0.25 : speed;
    const t = time * animSpeed;

    // 1. Dark Void Base Layer
    ctx.fillStyle = '#05070a';
    ctx.fillRect(0, 0, width, height);

    // Deep cosmic horizon gradient
    const horizonGrad = ctx.createLinearGradient(0, 0, 0, height);
    horizonGrad.addColorStop(0, 'rgba(8, 12, 22, 0.6)');
    horizonGrad.addColorStop(0.5, 'rgba(12, 18, 32, 0.3)');
    horizonGrad.addColorStop(1, 'rgba(5, 7, 10, 0.95)');
    ctx.fillStyle = horizonGrad;
    ctx.fillRect(0, 0, width, height);

    ctx.save();

    // 2. Grid Mesh Setup
    // Columns and rows scaled to gridDensity (clamped 10 to 80)
    const density = Math.max(10, Math.min(80, Math.round(gridDensity)));
    const cols = Math.max(12, Math.round(density * 0.7));
    const rows = Math.max(8, Math.round(density * 0.45));

    const cx = width * 0.5;
    const cy = height * 0.52;
    const fov = 350; // Perspective camera focal length

    // 2D Array of projected points: screenX, screenY, depth, tension
    const projectedNodes: Array<Array<{ sx: number; sy: number; z: number; tension: number; rawU: number; rawV: number }>> = [];

    const spacingX = width / (cols - 1);
    const spacingY = height / (rows - 1);

    // Compute 3D node positions, height displacement and nodal tension
    for (let r = 0; r < rows; r++) {
      const rowNodes: Array<{ sx: number; sy: number; z: number; tension: number; rawU: number; rawV: number }> = [];
      const vNorm = (r - rows / 2) / (rows / 2); // [-1, 1]

      for (let c = 0; c < cols; c++) {
        const uNorm = (c - cols / 2) / (cols / 2); // [-1, 1]

        // Manifold wave equation: harmonic interference + distortion ripple
        const wave1 = Math.sin(uNorm * 3.5 + t * 1.8) * Math.cos(vNorm * 3.0 - t * 1.2);
        const distFromCenter = Math.sqrt(uNorm * uNorm + vNorm * vNorm);
        const wave2 = Math.sin(distFromCenter * 5.0 - t * 2.8) * distortion;
        const wave3 = Math.cos((uNorm + vNorm) * 2.5 + t * 0.9) * 0.5;

        // Displaced Z (depth / elevation)
        const elevation = (wave1 * 35 * intensity) + (wave2 * 30) + (wave3 * 15);
        const zDepth = 120 + elevation; // Depth coordinate for perspective

        // Nodal tension metric: local spatial derivative magnitude |∇z|
        const gradU = Math.cos(uNorm * 3.5 + t * 1.8) * 3.5 * intensity + Math.cos(distFromCenter * 5.0 - t * 2.8) * 5.0 * distortion;
        const gradV = -Math.sin(vNorm * 3.0 - t * 1.2) * 3.0 * intensity;
        const rawTension = Math.min(1.0, Math.sqrt(gradU * gradU + gradV * gradV) / (4.5 + distortion * 3.0));

        // 3D Perspective Projection
        const worldX = (c - cols / 2) * spacingX * 1.15;
        const worldY = (r - rows / 2) * spacingY * 0.95;

        const scale = fov / (fov + zDepth);
        const screenX = cx + worldX * scale;
        const screenY = cy + worldY * scale;

        rowNodes.push({
          sx: screenX,
          sy: screenY,
          z: zDepth,
          tension: rawTension,
          rawU: c,
          rawV: r,
        });
      }
      projectedNodes.push(rowNodes);
    }

    // 3. Render Horizontal and Vertical Matrix Lines with Nodal Tension Shading
    ctx.lineWidth = 1.0;

    // Horizontal grid lines
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols - 1; c++) {
        const p1 = projectedNodes[r][c];
        const p2 = projectedNodes[r][c + 1];

        const avgTension = (p1.tension + p2.tension) * 0.5;
        const baseAlpha = (0.12 + intensity * 0.28) * (0.4 + 0.6 * (1 - (p1.z / 250)));

        if (avgTension > 0.65 && distortion > 0.15) {
          // High tension: Amber to Crimson
          ctx.strokeStyle = `rgba(255, 183, 0, ${Math.min(1, baseAlpha * 1.5)})`;
        } else if (avgTension > 0.4) {
          // Medium tension: Violet
          ctx.strokeStyle = `rgba(157, 78, 221, ${Math.min(1, baseAlpha * 1.2)})`;
        } else {
          // Calm: Cyan
          ctx.strokeStyle = `rgba(0, 243, 255, ${Math.min(1, baseAlpha)})`;
        }

        ctx.beginPath();
        ctx.moveTo(p1.sx, p1.sy);
        ctx.lineTo(p2.sx, p2.sy);
        ctx.stroke();
      }
    }

    // Vertical grid lines
    for (let c = 0; c < cols; c++) {
      for (let r = 0; r < rows - 1; r++) {
        const p1 = projectedNodes[r][c];
        const p2 = projectedNodes[r + 1][c];

        const avgTension = (p1.tension + p2.tension) * 0.5;
        const baseAlpha = (0.10 + intensity * 0.25) * (0.4 + 0.6 * (1 - (p1.z / 250)));

        if (avgTension > 0.65 && distortion > 0.15) {
          ctx.strokeStyle = `rgba(255, 42, 75, ${Math.min(1, baseAlpha * 1.6)})`;
        } else {
          ctx.strokeStyle = `rgba(0, 243, 255, ${Math.min(1, baseAlpha * 0.85)})`;
        }

        ctx.beginPath();
        ctx.moveTo(p1.sx, p1.sy);
        ctx.lineTo(p2.sx, p2.sy);
        ctx.stroke();
      }
    }

    // 4. Coordinate Rewiring Vectors and Telemetry Calipers
    const sampleStep = Math.max(2, Math.round(cols / 8));
    for (let r = 1; r < rows - 1; r += 2) {
      for (let c = 1; c < cols - 1; c += sampleStep) {
        const node = projectedNodes[r][c];
        const tension = node.tension;

        // Draw nodal intersection indicator
        const dotRadius = 1.5 + tension * 2.5;
        const dotAlpha = 0.3 + tension * 0.7;

        if (tension > 0.5) {
          ctx.fillStyle = `rgba(255, 183, 0, ${dotAlpha})`;
        } else {
          ctx.fillStyle = `rgba(0, 243, 255, ${dotAlpha})`;
        }
        ctx.beginPath();
        ctx.arc(node.sx, node.sy, dotRadius, 0, Math.PI * 2);
        ctx.fill();

        // Coordinate Rewiring Vector
        if (tension > 0.35 && !reducedMotion) {
          const vectorAngle = t * 2.2 + node.rawU * 0.7 + node.rawV * 0.9;
          const vectorLen = (6 + distortion * 16) * (0.6 + tension * 0.8);
          const endX = node.sx + Math.cos(vectorAngle) * vectorLen;
          const endY = node.sy + Math.sin(vectorAngle) * vectorLen;

          ctx.strokeStyle = tension > 0.7
            ? `rgba(255, 42, 75, ${0.75 * intensity})`
            : `rgba(0, 243, 255, ${0.65 * intensity})`;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(node.sx, node.sy);
          ctx.lineTo(endX, endY);
          ctx.stroke();

          // Arrowhead / rewiring caliper notch
          const arrowAngle1 = vectorAngle + Math.PI * 0.85;
          const arrowAngle2 = vectorAngle - Math.PI * 0.85;
          const arrowLen = 3.5;
          ctx.beginPath();
          ctx.moveTo(endX + Math.cos(arrowAngle1) * arrowLen, endY + Math.sin(arrowAngle1) * arrowLen);
          ctx.lineTo(endX, endY);
          ctx.lineTo(endX + Math.cos(arrowAngle2) * arrowLen, endY + Math.sin(arrowAngle2) * arrowLen);
          ctx.stroke();
        }

        // Ledger coordinate crosshair
        if (tension > 0.6 && distortion > 0.2) {
          ctx.strokeStyle = `rgba(255, 183, 0, 0.4)`;
          ctx.lineWidth = 0.8;
          const chSize = 4;
          ctx.beginPath();
          ctx.moveTo(node.sx - chSize, node.sy);
          ctx.lineTo(node.sx + chSize, node.sy);
          ctx.moveTo(node.sx, node.sy - chSize);
          ctx.lineTo(node.sx, node.sy + chSize);
          ctx.stroke();
        }
      }
    }

    ctx.restore();
  }
}
